/*
 * Clients: the referrals Wealthoak turned into accounts (GET /clients). A
 * partner sees the name, account status and plan, and what they have earned
 * from the client; never a portfolio or holdings.
 */

import { useState } from "react";
import { clients, type ClientSummary } from "../api/client";
import { useLoad } from "../api/useLoad";
import { EmptyState, LoadError, SkeletonRows, Status, accountStatus, subscriptionStatus } from "./bits";
import ClientSheet from "./ClientSheet";
import { fmtDate, inr, plural } from "./format";
import { ArrowRight, Lock, Users } from "./icons";
import type { View } from "./Shell";
import "./clients.css";

export default function ClientsWorkspace({ onNavigate }: { onNavigate: (view: View) => void }) {
  const list = useLoad(() => clients.list());
  const rows = list.data?.items ?? [];
  const [open, setOpen] = useState<ClientSummary | null>(null);
  const subscribed = rows.filter((c) => c.subscription_status === "active").length;
  const earned = rows.reduce((s, c) => s + (Number(c.commission_total) || 0), 0);
  const inProgress = rows.reduce((s, c) => s + (Number(c.commission_in_progress) || 0), 0);

  return (
    <>
      <section className="page-heading">
        <div>
          <p className="eyebrow">Clients</p>
          <h1>Your clients</h1>
          <p className="page-heading__sub">
            {list.data && rows.length > 0 ? (
              <>
                {plural(rows.length, "client")} you brought to Wealthoak, <strong>{subscribed}</strong> on an active
                plan. You&apos;ve earned <strong>{inr(earned)}</strong> from them so far
                {inProgress > 0 && <>, with {inr(inProgress)} more in progress</>}.
              </>
            ) : (
              "People you referred who now have a Wealthoak account."
            )}
          </p>
        </div>
      </section>

      <section className="card table-card" aria-label="Clients" aria-busy={list.loading}>
        {list.error ? (
          <div className="table-card__state">
            <LoadError message={list.error} onRetry={list.reload} />
          </div>
        ) : !list.data ? (
          <SkeletonRows rows={4} label="Loading clients" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No clients yet"
            action={
              <button className="button button--secondary" onClick={() => onNavigate("referrals")}>
                See your referrals <ArrowRight size={15} />
              </button>
            }
          >
            Clients appear here once Wealthoak opens an account for someone you referred. From then on you earn a
            share of every subscription payment they make.
          </EmptyState>
        ) : (
          <table className="data-table is-stacked">
            <thead>
              <tr>
                <th scope="col">Client</th>
                <th scope="col">Account</th>
                <th scope="col">Plan</th>
                <th scope="col">Subscription</th>
                <th scope="col">Client since</th>
                <th scope="col" className="r">Commission earned</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <ClientRow key={c.referral_id} c={c} onOpen={() => setOpen(c)} />
              ))}
            </tbody>
          </table>
        )}
      </section>

      <p className="notice page-foot">
        <Lock size={15} />
        <span>
          For your clients&apos; privacy you see their name, account status and plan, never their portfolio,
          holdings or payments beyond the commission they earn you.
        </span>
      </p>

      {open && (
        <ClientSheet
          c={open}
          nameIsUnique={rows.filter((r) => r.client_name === open.client_name).length === 1}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

function ClientRow({ c, onOpen }: { c: ClientSummary; onOpen: () => void }) {
  const acct = accountStatus(c.account_status);
  const sub = subscriptionStatus(c.subscription_status);
  return (
    // The row is clickable for mouse users; the name button is the keyboard and screen-reader way in.
    <tr className="client-row" onClick={onOpen}>
      <td className="is-title">
        <button type="button" className="client-open" onClick={onOpen} aria-haspopup="dialog">
          {c.client_name}
        </button>
      </td>
      <td data-label="Account">
        <Status tone={acct.tone}>{acct.label}</Status>
      </td>
      <td data-label="Plan">
        <span className={`chip${c.plan_name ? " is-gold" : ""}`}>{c.plan_name ?? "No plan yet"}</span>
      </td>
      <td data-label="Subscription">
        <span>
          <Status tone={sub.tone}>{sub.label}</Status>
          {c.subscribed_until && (
            <small>
              {c.subscription_status === "active" || c.subscription_status === "trialing" ? "Until" : "Ended"}{" "}
              {fmtDate(c.subscribed_until)}
            </small>
          )}
        </span>
      </td>
      <td data-label="Client since">{fmtDate(c.client_since)}</td>
      <td data-label="Commission earned" className="r">
        <span className="cell-strong">{inr(c.commission_total)}</span>
        {Number(c.commission_in_progress) > 0 && <small>{inr(c.commission_in_progress)} in progress</small>}
      </td>
    </tr>
  );
}
